(() => {
  const tag = 'home-video-play-switch';
  if (customElements.get(tag)) return;
  const titles = new Map();
  const scrollOwners = new Set();
  let restoreScroll;
  function lockScroll(owner) {
    if (scrollOwners.has(owner)) return;
    if (!scrollOwners.size) {
      const x = window.scrollX, y = window.scrollY;
      const root = document.documentElement, body = document.body;
      const gap = Math.max(0, window.innerWidth - root.clientWidth);
      const saved = [];
      const set = (element, property, value) => {
        saved.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
        element.style.setProperty(property, value, 'important');
      };
      const padding = getComputedStyle(body).paddingRight;
      set(root, 'overflow', 'hidden');
      set(body, 'position', 'fixed');
      set(body, 'top', `-${y}px`);
      set(body, 'left', `-${x}px`);
      set(body, 'width', '100%');
      set(body, 'box-sizing', 'border-box');
      set(body, 'overflow', 'hidden');
      if (gap) set(body, 'padding-right', `calc(${padding} + ${gap}px)`);
      restoreScroll = () => {
        for (const [element, property, value, priority] of saved) {
          if (value) element.style.setProperty(property, value, priority);
          else element.style.removeProperty(property);
        }
        window.scrollTo({ left: x, top: y, behavior: 'instant' });
      };
    }
    scrollOwners.add(owner);
  }
  function unlockScroll(owner) {
    if (!scrollOwners.delete(owner) || scrollOwners.size) return;
    restoreScroll?.();
    restoreScroll = null;
  }
  function getTitle(id) {
    if (titles.has(id)) return titles.get(id);
    const url = new URL('https://www.youtube.com/oembed');
    url.searchParams.set('url', `https://www.youtube.com/watch?v=${id}`);
    url.searchParams.set('format', 'json');
    const task = fetch(url, { signal: AbortSignal.timeout(8000), credentials: 'omit' })
      .then(response => { if (!response.ok) throw new Error('Title unavailable'); return response.json(); })
      .then(data => { if (typeof data.title !== 'string') throw new Error('Title unavailable'); return data.title; })
      .catch(() => { titles.delete(id); return ''; });
    titles.set(id, task);
    return task;
  }
  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-video-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-video-player]');
      this.cards = [...this.querySelectorAll('[data-video-id]')];
      this.cards.forEach(card => {
        card.addEventListener('click', () => this.open(card), { signal });
        const id = card.dataset.videoId;
        if (!card.dataset.customTitle && /^[\w-]{11}$/.test(id)) {
          getTitle(id).then(title => {
            if (signal.aborted || !title || card.dataset.customTitle || card.dataset.videoId !== id) return;
            card.querySelector('[data-video-title]').textContent = title;
            card.setAttribute('aria-label', `Play ${title}`);
          });
        }
      });
      this.querySelector('[data-video-previous]').addEventListener('click', () => this.move(-1), { signal });
      this.querySelector('[data-video-next]').addEventListener('click', () => this.move(1), { signal });
      this.querySelector('[data-video-close]').addEventListener('click', () => { this.dialog.close(); this.clearPlayer(); }, { signal });
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.clearPlayer(); }, { signal });
      // Native dialog supports Escape; dispose the player immediately, even before the close event.
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dialog.close(); this.clearPlayer(); }, { signal });
      this.track.addEventListener('scroll', () => this.updateEdges(), { signal, passive: true });
      this.observer = new ResizeObserver(() => this.updateEdges());
      this.observer.observe(this.track);
      this.updateEdges();
    }
    move(direction) {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
    }
    updateEdges() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.querySelector('[data-video-edge="previous"]').hidden = this.track.scrollLeft <= 1;
      this.querySelector('[data-video-edge="next"]').hidden = this.track.scrollLeft >= maximum - 1;
    }
    open(card) {
      const id = card.dataset.videoId;
      if (!/^[\w-]{11}$/.test(id)) return;
      this.player.replaceChildren();
      this.opener = card;
      this.cards.forEach(item => item.removeAttribute('aria-current'));
      card.setAttribute('aria-current', 'true');
      const iframe = document.createElement('iframe');
      const url = new URL(`https://www.youtube.com/embed/${id}`);
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('mute', '0');
      url.searchParams.set('start', '0');
      url.searchParams.set('playsinline', '1');
      iframe.src = url.href;
      iframe.title = card.querySelector('[data-video-title]').textContent.trim() || 'YouTube video player';
      iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.player.append(iframe);
      lockScroll(this);
      try {
        if (!this.dialog.open) this.dialog.showModal();
      } catch (error) {
        this.clearPlayer();
        throw error;
      }
    }
    clearPlayer() {
      this.player?.replaceChildren();
      unlockScroll(this);
      if (this.opener?.isConnected && this.isConnected) this.opener.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.clearPlayer();
      this.controller = null;
    }
  });
})();
